import styled from "@emotion/styled";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import {
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Stack
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { MagnifyingGlass } from "@phosphor-icons/react";
import { LoadingScreen } from "components/LoadingScreen";
import { Button, DeleteModal, TextField } from "components/UI";
import { useChurchStore } from "features/churches";
import {
  useCampaign,
  useDeletePledge,
  usePledgesPaginated
} from "features/finances";
import moment from "moment";
import { parseAsString, useQueryState } from "nuqs";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useDebounce } from "react-use";
import { useCentralStore } from "stores";
import { EthiopianDateField } from "../../components/EthiopianDateField";
import { GoalCard, PledgeCard } from "../Cards";

const Container = styled.div`
  grid-column: 2 / 3;
  grid-row: 1 / 3;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

const Title = styled.h6`
  margin: 0;
  font-size: 12px;
  font-weight: 600;
`;

const Subtitle = styled.p`
  margin: 0;
  font-size: 12px;
`;

const PrimaryActions = styled.div`
  display: flex;
  gap: 8px;
`;

const SecondaryActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  width: 100%;
`;

const SearchIcon = styled(MagnifyingGlass)`
  color: ${({ theme }) => theme.palette.primary.main};
`;

export const PledgeTable = ({ onEdit, onOpenModal }) => {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [startDate, setStartDate] = useQueryState(
    "startDate",
    parseAsString.withDefault(moment().startOf("month").format("YYYY-MM-DD"))
  );
  const [endDate, setEndDate] = useQueryState(
    "endDate",
    parseAsString.withDefault(moment().format("YYYY-MM-DD"))
  );
  const selectedChurch = useChurchStore(state => state.selectedChurch);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedPledge, setSelectedPledge] = useState(null);
  const openDeleteModal = useCentralStore(state => state.openDeleteModal);
  const deletePledgeMutation = useDeletePledge();

  const { id } = useParams();

  const { data: campaign } = useCampaign({
    churchId: selectedChurch?.id,
    campaignId: id
  });

  const pledgeQuery = usePledgesPaginated({
    churchId: selectedChurch?.id,
    campaignId: id,
    page,
    pageSize,
    queries: {
      search: debouncedSearch,
      startDate,
      endDate
    }
  });

  useDebounce(() => {
    setDebouncedSearch(search);
  }, 500);

  const handleMenuOpen = (event, row) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedPledge(row);
  };

  const handleMenuClose = event => {
    if (event) {
      event.stopPropagation();
    }
    setAnchorEl(null);
  };

  const handleEdit = event => {
    event.stopPropagation();
    onEdit(selectedPledge);
    onOpenModal();
    handleMenuClose();
  };

  const handleDelete = event => {
    event.stopPropagation();
    openDeleteModal();
    handleMenuClose();
  };

  const columns = [
    {
      field: "full_name",
      headerName: "Full Name",
      flex: 1,
      minWidth: 300,
      sortable: false,
      renderCell: ({ row }) => {
        const memberNames = row.members
          .map(member => `${member.first_name} ${member.father_name}`)
          .join(", ");

        return (
          <span>{memberNames.length > 0 ? memberNames : row.full_name}</span>
        );
      }
    },
    {
      field: "phone_number",
      headerName: "Phone Number",
      flex: 1,
      minWidth: 300,
      sortable: false,
      renderCell: ({ row }) => {
        const memberPhoneNumbers = row.members
          .map(member => member.phone)
          .join(", ");

        return (
          <span>
            {memberPhoneNumbers.length > 0
              ? memberPhoneNumbers
              : row.phone_number}
          </span>
        );
      }
    },
    {
      field: "pledge_frequency",
      headerName: "Frequency",
      flex: 1,
      sortable: false
    },
    {
      field: "start_date",
      headerName: "Start Date",
      flex: 1,
      sortable: false,
      renderCell: ({ row }) => {
        return <span>{moment(row.start_date).format("DD-MM-YYYY")}</span>;
      }
    },
    {
      field: "end_date",
      headerName: "End Date",
      flex: 1,
      sortable: false,
      renderCell: ({ row }) => {
        return <span>{moment(row.end_date).format("DD-MM-YYYY")}</span>;
      }
    },
    {
      field: "pledge_amount",
      headerName: "Pledged",
      flex: 1,
      sortable: false,
      valueFormatter: ({ value }) => {
        return Number(value).toLocaleString();
      }
    },
    {
      field: "paid_amount",
      headerName: "Paid",
      flex: 1,
      sortable: false,
      valueFormatter: ({ value }) => {
        return Number(value).toLocaleString();
      }
    },
    {
      field: "remaining_amount",
      headerName: "Remaining",
      flex: 1,
      sortable: false,
      renderCell: ({ row }) => {
        return (
          <span>
            {(
              Number(row.pledge_amount || 0) - Number(row.paid_amount || 0)
            ).toLocaleString()}
          </span>
        );
      }
    },
    {
      field: "action",
      headerName: "Action",
      flex: 1,
      sortable: false,
      renderCell: ({ row }) => {
        return (
          <IconButton
            onClick={event => handleMenuOpen(event, row)}
            size="small"
          >
            <MoreVertIcon />
          </IconButton>
        );
      }
    }
  ];

  if (pledgeQuery.isLoading) {
    return <LoadingScreen />;
  }

  return (
    <Container>
      <DeleteModal
        title="Delete Pledge"
        name={selectedPledge?.full_name || ""}
        onDelete={() => {
          deletePledgeMutation.mutate({
            churchId: selectedChurch?.id,
            campaignId: id,
            pledgeId: selectedPledge?.id
          });
        }}
      />
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleEdit}>Edit</MenuItem>
        <MenuItem onClick={handleDelete} sx={{ color: "error.main" }}>
          Delete
        </MenuItem>
      </Menu>
      <Stack alignItems="flex-start" spacing={4}>
        <Header>
          <Stack>
            <Title>{campaign?.campaign_name}</Title>
            <Subtitle>{campaign?.purpose}</Subtitle>
          </Stack>
          <PrimaryActions>
            <Button onClick={onOpenModal}>Add Pledge</Button>
          </PrimaryActions>
        </Header>
        <Stack direction="row" spacing={2} sx={{ alignSelf: "stretch" }}>
          <PledgeCard
            title="Total Pledgers"
            stat={pledgeQuery.data?.result?.total || 0}
          />
          <GoalCard
            pledged={pledgeQuery.data?.result?.total_pledge_amount || 0}
            raised={pledgeQuery.data?.result?.total_paid_amount || 0}
            target={campaign?.goal_amount}
          />
        </Stack>
        <SecondaryActions>
          <Stack direction="row" alignItems="center" spacing={2}>
            <EthiopianDateField
              size="small"
              label="Start date"
              value={startDate}
              onChange={e => setStartDate(e.target.value)}
            />
            <EthiopianDateField
              size="small"
              label="End date"
              value={endDate}
              onChange={e => setEndDate(e.target.value)}
            />
          </Stack>
          <TextField
            neutral={1}
            placeholder="Search pledge"
            register={() => {}}
            InputProps={{
              endAdornment: (
                <InputAdornment position="start">
                  <SearchIcon size={16} />
                </InputAdornment>
              )
            }}
            autoComplete="off"
            value={search}
            onChange={e => setSearch(e.target.value)}
            width="auto"
          />
        </SecondaryActions>
      </Stack>

      <DataGrid
        autoHeight
        rows={pledgeQuery.data?.result?.data || []}
        rowCount={pledgeQuery.data?.result?.total || 0}
        loading={pledgeQuery.isLoading}
        columns={columns}
        pagination
        rowsPerPageOptions={[5, 10, 15, 25, 50, 100]}
        density="comfortable"
        getRowId={row => row.id}
        disableColumnMenu
        disableSelectionOnClick
        paginationMode="server"
        page={page - 1}
        pageSize={pageSize}
        onPageChange={newPage => setPage(newPage + 1)}
        onPageSizeChange={newPageSize => setPageSize(newPageSize)}
        localeText={{
          noRowsLabel: t("teams.teamProfile.NoRows"),
          MuiTablePagination: {
            labelRowsPerPage: t("teams.teamProfile.RowsPerPage")
          }
        }}
      />
    </Container>
  );
};
